import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Upload, Video, Heart, Eye, Trash2, X, Play, Pause,
  Share2, Download, Clock, FileVideo, TrendingUp, ChevronUp, ChevronDown,
  MessageCircle, Bookmark, MoreHorizontal
} from 'lucide-react';
import { getAuthHeaders } from '../lib/auth';
import Navigation from '../components/Navigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityVideos = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const videoRefs = useRef([]);
  const containerRef = useRef(null);
  const viewedVideos = useRef(new Set()); // Track which videos have been viewed

  // Upload form
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    file: null
  });

  useEffect(() => {
    loadVideos();
  }, []);

  // Ensure first video plays with sound when loaded
  useEffect(() => {
    if (videos.length > 0 && videoRefs.current[0]) {
      const firstVideo = videoRefs.current[0];
      firstVideo.muted = false;
      firstVideo.volume = 1.0;
    }
  }, [videos]);

  useEffect(() => {
    // Play current video, pause and mute others
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentVideoIndex) {
          // Stop all other videos first
          videoRefs.current.forEach((v, i) => {
            if (v && i !== index) {
              v.pause();
              v.currentTime = 0;
              v.muted = true; // Mute non-active videos
            }
          });
          
          // Reset and play current video with sound
          video.pause();
          video.currentTime = 0;
          video.muted = false; // Ensure sound is on for current video
          video.volume = 1.0; // Full volume
          
          // Small delay to ensure proper loading
          setTimeout(() => {
            video.play().catch((err) => {
              console.log('Auto-play failed:', err);
              // Show play button or handle user interaction requirement
            });
          }, 100);
          
          // Track view when video comes into view
          if (videos[index]) {
            trackView(videos[index].video_id);
          }
        } else {
          // Pause and mute non-current videos
          video.pause();
          video.currentTime = 0;
          video.muted = true;
        }
      }
    });
  }, [currentVideoIndex]);

  useEffect(() => {
    // Scroll detection - detect which video is in view
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;

      const videos = container.querySelectorAll('[data-video-index]');
      let closestVideo = null;
      let closestDistance = Infinity;

      videos.forEach((videoElement) => {
        const rect = videoElement.getBoundingClientRect();
        const distance = Math.abs(rect.top);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestVideo = videoElement;
        }
      });

      if (closestVideo) {
        const index = parseInt(closestVideo.getAttribute('data-video-index'));
        if (index !== currentVideoIndex) {
          setCurrentVideoIndex(index);
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [currentVideoIndex]);

  useEffect(() => {
    // Keyboard navigation
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowUp') {
        scrollToPrevious();
      } else if (e.key === 'ArrowDown') {
        scrollToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentVideoIndex, videos.length]);

  const scrollToNext = () => {
    if (currentVideoIndex < videos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
      const nextVideo = document.getElementById(`video-${currentVideoIndex + 1}`);
      if (nextVideo) {
        nextVideo.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const scrollToPrevious = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
      const prevVideo = document.getElementById(`video-${currentVideoIndex - 1}`);
      if (prevVideo) {
        prevVideo.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const loadVideos = async () => {
    try {
      const response = await axios.get(`${API}/community/videos`);
      setVideos(response.data.videos || []);
    } catch (error) {
      toast.error('Failed to load videos');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      toast.error('Please select a video file');
      return;
    }

    // Validate file size (100MB max)
    if (file.size > 100 * 1024 * 1024) {
      toast.error('Video must be under 100MB');
      return;
    }

    // Get video duration
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = function() {
      window.URL.revokeObjectURL(video.src);
      const duration = video.duration;

      if (duration > 90) {
        toast.error('Video must be 90 seconds or less');
        return;
      }

      setUploadData({ ...uploadData, file });
      toast.success(`Video selected: ${file.name}`);
    };
    video.src = URL.createObjectURL(file);
  };

  const handleUpload = async () => {
    if (!uploadData.file || !uploadData.title) {
      toast.error('Please provide a title and select a video');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('title', uploadData.title);
      formData.append('description', uploadData.description);
      formData.append('user_email', localStorage.getItem('email') || 'user@example.com');

      const response = await axios.post(`${API}/community/upload-video`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success(`Video uploaded! Saved ${response.data.compression_ratio} with compression`);
      setShowUploadModal(false);
      setUploadData({ title: '', description: '', file: null });
      loadVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleLike = async (videoId) => {
    const userEmail = localStorage.getItem('email') || 'user@example.com';
    const video = videos.find(v => v.video_id === videoId);
    const currentLikes = video?.likes || [];
    const isLiked = currentLikes.includes(userEmail);

    // Optimistic update - update UI immediately
    setVideos(videos.map(v => 
      v.video_id === videoId 
        ? { 
            ...v, 
            likes: isLiked 
              ? currentLikes.filter(e => e !== userEmail)
              : [...currentLikes, userEmail]
          }
        : v
    ));

    try {
      const formData = new FormData();
      formData.append('user_email', userEmail);

      const response = await axios.post(
        `${API}/community/videos/${videoId}/like`,
        formData,
        { headers: getAuthHeaders() }
      );

      // Update with server response (in case of discrepancy)
      setVideos(videos.map(v => 
        v.video_id === videoId 
          ? { ...v, likes: response.data.liked 
              ? [...currentLikes.filter(e => e !== userEmail), userEmail] 
              : currentLikes.filter(e => e !== userEmail)
            }
          : v
      ));

      toast.success(response.data.liked ? 'Liked!' : 'Unliked');
    } catch (error) {
      // Revert optimistic update on error
      setVideos(videos.map(v => 
        v.video_id === videoId 
          ? { ...v, likes: currentLikes }
          : v
      ));
      toast.error('Failed to like video');
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm('Delete this video? This cannot be undone.')) return;

    try {
      const formData = new FormData();
      formData.append('user_email', localStorage.getItem('email') || 'user@example.com');

      await axios.delete(`${API}/community/videos/${videoId}`, {
        data: formData,
        headers: getAuthHeaders()
      });

      toast.success('Video deleted');
      loadVideos();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete video');
    }
  };

  const trackView = async (videoId) => {
    // Only track if not already viewed in this session
    if (viewedVideos.current.has(videoId)) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('user_email', localStorage.getItem('email') || 'user@example.com');

      const response = await axios.post(
        `${API}/community/videos/${videoId}/view`,
        formData,
        { headers: getAuthHeaders() }
      );

      // Mark as viewed
      viewedVideos.current.add(videoId);

      // Update local state with new view count
      setVideos(videos.map(v => 
        v.video_id === videoId 
          ? { ...v, views: response.data.views }
          : v
      ));
    } catch (error) {
      // Silent fail - don't show error toast for view tracking
      console.error('Failed to track view:', error);
    }
  };

  const shareVideo = (video) => {
    const shareUrl = `${window.location.origin}/community/videos/${video.video_id}`;
    
    if (navigator.share) {
      navigator.share({
        title: video.title,
        text: video.description,
        url: shareUrl
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
      <div className="relative h-screen w-full bg-black overflow-hidden">
      {/* Top Header Bar */}
      <div className="absolute top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/60 to-transparent p-4">
        <div className="flex items-center justify-between max-w-screen-xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/20 p-2 rounded-full transition"
          >
            <X className="w-6 h-6" />
          </button>
          <h1 className="text-white font-bold text-lg">Community Videos</h1>
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            size="sm"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload
          </Button>
        </div>
      </div>

      {/* Short-Form Video Container */}
      <div 
        ref={containerRef}
        className="h-full w-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide"
        style={{ scrollSnapType: 'y mandatory' }}
      >
        {loading ? (
          <div className="h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
          </div>
        ) : videos.length === 0 ? (
          <div className="h-screen flex items-center justify-center px-4">
            <div className="text-center">
              <FileVideo className="w-20 h-20 text-white/60 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">No videos yet</h3>
              <p className="text-white/80 mb-6">Be the first to share a video short!</p>
              <Button
                onClick={() => setShowUploadModal(true)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                <Upload className="w-5 h-5 mr-2" />
                Upload First Video
              </Button>
            </div>
          </div>
        ) : (
          videos.map((video, index) => {
            const userLiked = video.likes?.includes(localStorage.getItem('email'));
            const isOwner = video.user_email === localStorage.getItem('email');

            return (
              <div
                key={video.video_id}
                id={`video-${index}`}
                className="relative h-screen w-full snap-start flex items-center justify-center"
              >
                {/* Video Player */}
                <video
                  ref={(el) => (videoRefs.current[index] = el)}
                  src={video.video_url}
                  className="h-full w-full object-contain bg-black"
                  loop
                  playsInline
                  onClick={(e) => {
                    if (e.target.paused) {
                      e.target.play();
                    } else {
                      e.target.pause();
                    }
                  }}
                />

                {/* Right Side Actions */}
                <div className="absolute right-4 bottom-24 flex flex-col gap-6 z-40">
                  {/* Like Button */}
                  <button
                    onClick={() => handleLike(video.video_id)}
                    className="flex flex-col items-center gap-1 text-white"
                  >
                    <div className={`p-3 rounded-full ${userLiked ? 'bg-pink-600' : 'bg-white/20 backdrop-blur-sm'} hover:scale-110 transition`}>
                      <Heart className={`w-7 h-7 ${userLiked ? 'fill-current' : ''}`} />
                    </div>
                    <span className="text-xs font-semibold">{video.likes?.length || 0}</span>
                  </button>

                  {/* Share Button */}
                  <button
                    onClick={() => shareVideo(video)}
                    className="flex flex-col items-center gap-1 text-white"
                  >
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-full hover:scale-110 transition">
                      <Share2 className="w-7 h-7" />
                    </div>
                    <span className="text-xs font-semibold">Share</span>
                  </button>

                  {/* Views */}
                  <div className="flex flex-col items-center gap-1 text-white">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-full">
                      <Eye className="w-7 h-7" />
                    </div>
                    <span className="text-xs font-semibold">{video.views || 0}</span>
                  </div>

                  {/* Delete (Owner Only) */}
                  {isOwner && (
                    <button
                      onClick={() => handleDelete(video.video_id)}
                      className="flex flex-col items-center gap-1 text-white"
                    >
                      <div className="p-3 bg-red-600/80 backdrop-blur-sm rounded-full hover:scale-110 transition">
                        <Trash2 className="w-7 h-7" />
                      </div>
                      <span className="text-xs font-semibold">Delete</span>
                    </button>
                  )}
                </div>

                {/* Bottom Info Overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent z-30">
                  <h3 className="text-white font-bold text-lg mb-2">{video.title}</h3>
                  {video.description && (
                    <p className="text-white/90 text-sm mb-3 line-clamp-2">{video.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-white/80 text-xs">
                    <span>@{video.user_email?.split('@')[0]}</span>
                    {video.compression_ratio && (
                      <span className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        {video.compression_ratio} saved
                      </span>
                    )}
                  </div>
                </div>

                {/* Removed scroll buttons - now scroll-based */}
              </div>
            );
          })
        )}
      </div>


      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <Card className="max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-slate-900">Upload Video Short</h2>
              <button onClick={() => setShowUploadModal(false)}>
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>

            <div className="space-y-4">
              {/* File Upload */}
              <div>
                <Label>Video File (up to 90 seconds, max 100MB)</Label>
                <div className="mt-2">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    id="video-upload"
                  />
                  <label
                    htmlFor="video-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-purple-500 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                    <span className="text-sm text-slate-600">
                      {uploadData.file ? uploadData.file.name : 'Click to select video'}
                    </span>
                    {uploadData.file && (
                      <span className="text-xs text-slate-500 mt-1">
                        {formatFileSize(uploadData.file.size)}
                      </span>
                    )}
                  </label>
                </div>
              </div>

              {/* Title */}
              <div>
                <Label>Title *</Label>
                <Input
                  value={uploadData.title}
                  onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })}
                  placeholder="Enter video title"
                  maxLength={100}
                />
              </div>

              {/* Description */}
              <div>
                <Label>Description (Optional)</Label>
                <Textarea
                  value={uploadData.description}
                  onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                  placeholder="Describe your video..."
                  rows={3}
                  maxLength={500}
                />
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div>
                  <div className="flex justify-between text-sm text-slate-600 mb-2">
                    <span>Uploading & Compressing...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleUpload}
                  disabled={uploading || !uploadData.file || !uploadData.title}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  {uploading ? 'Uploading...' : 'Upload Video'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      </div>
    </>
  );
};

export default CommunityVideos;
